import React, { useEffect, useState } from "react";
import { Form, Input, Modal, Switch } from "../../../design-system";
import { handleError } from "../../../common/ErrorHandling";
import { FeatureFlag } from "../../../types/UserGroup";
import classes from "./CreateOrganizationModal.module.less";
import detailClasses from "./OrganizationsView.module.less";
import {
    CreateOrganizationRequest,
    ORG_DOMAIN_MAX_LENGTH,
    ORG_DOMAIN_MIN_LENGTH,
    ORG_DOMAIN_PATTERN,
    suggestOrgDomain,
} from "../../../types/AdminDashboardTypes";

interface CreateOrganizationModalProps {
    open: boolean;
    onCancel: () => void;
    /** Creates the org; resolving means success and the dialog can close. */
    onCreate: (request: CreateOrganizationRequest) => Promise<void>;
}

interface FormValues {
    orgName: string;
    domain: string;
}

// The flags an org can be granted, straight from the enum: NONE is its
// placeholder for "no flags exist right now", not a grantable flag. An empty
// list simply shows no toggles.
const AVAILABLE_FLAGS = Object.values(FeatureFlag).filter((flag) => flag !== FeatureFlag.NONE);

export const CreateOrganizationModal: React.FC<CreateOrganizationModalProps> = ({
    open,
    onCancel,
    onCreate,
}) => {
    const [form] = Form.useForm<FormValues>();
    const [loading, setLoading] = useState(false);
    // The domain follows the name until the user edits it themselves, at which
    // point their value wins for the rest of the dialog.
    const [domainEdited, setDomainEdited] = useState(false);
    // Every flag starts enabled and the user switches off what this org
    // should not get.
    const [enabledFlags, setEnabledFlags] = useState<ReadonlySet<FeatureFlag>>(
        () => new Set(AVAILABLE_FLAGS)
    );

    // destroyOnClose rebuilds the fields on every open, so the "user owns the
    // domain field" flag and the toggles have to be reset alongside them.
    useEffect(() => {
        if (!open) return;
        setDomainEdited(false);
        setEnabledFlags(new Set(AVAILABLE_FLAGS));
    }, [open]);

    const toggleFlag = (flag: FeatureFlag, enabled: boolean) => {
        setEnabledFlags((pre) => {
            const next = new Set(pre);
            if (enabled) {
                next.add(flag);
            } else {
                next.delete(flag);
            }
            return next;
        });
    };

    // "some_flag" reads as "Some flag" on its toggle row.
    const flagLabel = (flag: string) => {
        const words = flag.replace(/[_-]+/g, " ");
        return words.charAt(0).toUpperCase() + words.slice(1);
    };

    const syncDomain = (changed: Partial<FormValues>) => {
        if (domainEdited || changed.orgName === undefined) return;
        form.setFieldsValue({ domain: suggestOrgDomain(changed.orgName) });
        // setFieldsValue skips validation, so without this a suggestion that
        // breaks the domain rules would only complain on submit. The rejection
        // is swallowed: the red field text is the feedback.
        form.validateFields(['domain']).catch(() => undefined);
    };

    const handleSubmit = async (values: FormValues) => {
        setLoading(true);
        try {
            await onCreate({
                orgName: values.orgName,
                domain: values.domain,
                featureFlags: AVAILABLE_FLAGS.filter((flag) => enabledFlags.has(flag)),
            });
            form.resetFields();
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            open={open}
            onCancel={onCancel}
            title="Add organization"
            destroyOnClose
            okText="Create"
            confirmLoading={loading}
            onOk={() => form.submit()}
        >
            <Form
                form={form}
                layout="vertical"
                requiredMark={false}
                disabled={loading}
                onValuesChange={syncDomain}
                onFinish={handleSubmit}
            >
                <Form.Item
                    label="Display name"
                    name="orgName"
                    extra="The name shown in the UI and reports."
                    rules={[{ required: true, message: "Please enter a name" }]}
                >
                    <Input autoFocus />
                </Form.Item>
                <Form.Item
                    label="Wristband tenant name"
                    name="domain"
                    extra="It cannot be changed later."
                    rules={[
                        { required: true, message: "Please enter a domain" },
                        {
                            min: ORG_DOMAIN_MIN_LENGTH,
                            max: ORG_DOMAIN_MAX_LENGTH,
                            message: `Must be ${ORG_DOMAIN_MIN_LENGTH} to ${ORG_DOMAIN_MAX_LENGTH} characters`,
                        },
                        {
                            pattern: ORG_DOMAIN_PATTERN,
                            message: "Lower-case letters and digits, optionally hyphen-separated",
                        },
                    ]}
                >
                    <Input onChange={() => setDomainEdited(true)} />
                </Form.Item>
                {AVAILABLE_FLAGS.length > 0 && (
                    <>
                        <div className={classes.sectionTitle}>Feature flags</div>
                        {AVAILABLE_FLAGS.map((flag) => (
                            <div key={flag} className={classes.toggleRow}>
                                {flagLabel(flag)}
                                <Switch
                                    size="small"
                                    checked={enabledFlags.has(flag)}
                                    onChange={(checked) => toggleFlag(flag, checked)}
                                />
                            </div>
                        ))}
                    </>
                )}
            </Form>
        </Modal>
    );
};
